import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import SimpleBarChart from '../SimpleBarChart';
import { useTheme } from '@hooks';
import PercentageLoadPerEngineTooltip from '@components/Charts/PercentageLoadPerEngineChart/PercentageLoadPerEngineTooltip';
import { getGeneratorSignalsMap } from '@api/helpers';
import { NoDataAvailable } from '@components/Display';

const getSecondAccessDef = (color) => {
  return {
    tickAmount: 5,
    opposite: true,
    forceNiceScale: true,
    title: {
      text: 'g/kWh',
      offsetY: -150,
      offsetX: -20,
      rotate: 0,
      style: {
        color: color,
        fontFamily: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(','),
        fontSize: '10px',
        fontWeight: 100
      }
    },
    decimalsInFloat: 0,
    labels: {
      offsetX: -8,
      offsetY: 0,
      style: {
        colors: [color],
        fontFamily: ['Roboto', 'helvetica', 'Arial', 'sans-serif'].join(',')
      }
    }
  };
};

const getValue = (val) => {
  if (val === null || val === 'null') {
    return 0;
  }

  return parseFloat(val);
};

function PercentageLoadPerEngineChart({ loading, powerMgmtData }) {
  const { theme } = useTheme();

  const { series, categories } = useMemo(() => {
    if (!powerMgmtData || !powerMgmtData.signals) return { series: [], categories: [] };
    const { signals } = powerMgmtData;
    const generatorMap = getGeneratorSignalsMap(signals);

    let load = [];
    let cat = [];
    let sfoc = [];
    
    Object.entries(generatorMap).forEach(([generatorNum, metrics]) => {
      cat.push(`Engine ${generatorNum}`);
      
      const loadData = metrics['Load_Summary'] || 0;
      const sfocData = metrics['SFOC_Summary'] || 0;
      load.push(loadData ? parseInt(loadData) : 0);
      sfoc.push(sfocData ? parseInt(sfocData) : 0);
    });

    return {
      categories: cat,
      series: [
        { name: 'Load', type: 'column', data: load },
        { name: 'SFOC', type: 'column', data: sfoc }
      ]
    };
  }, [powerMgmtData]);
  const secondaryAxis = useMemo(() => {
    const {
      palette: {
        charts: { twoColorsContrast }
      }
    } = theme;

    return getSecondAccessDef(twoColorsContrast[1]);
  }, [theme]);
  const {
    palette: {
      charts: { twoColorsContrast }
    }
  } = theme;

  if (!loading && NoDataAvailable.hasNoData(series)) {
    return <NoDataAvailable height="385px" />;
  }

  return (
    <SimpleBarChart
      colors={twoColorsContrast}
      loading={loading}
      tooltipUnit={'%'}
      secondaryAxis={secondaryAxis}
      primaryMaxValue={100}
      primaryXAxisLabel={'%'}
      primaryTitleOffsetY={25}
      height={385}
      categories={categories}
      legendOffsetY={0}
      series={series}
      splittingLabel={false}
      customTooltipContent={(series, dataPointIndex) => (
        <PercentageLoadPerEngineTooltip
          theme={theme}
          categories={categories}
          series={series}
          dataPointIndex={dataPointIndex}
        />
      )}
      id={'avg-engine-load-chart'}
    />
  );
}

PercentageLoadPerEngineChart.propTypes = {
  powerMgmtData: PropTypes.object,
  loading: PropTypes.bool
};

PercentageLoadPerEngineChart.defaultProps = {
  powerMgmtData: null,
  loading: false
};

export default PercentageLoadPerEngineChart;
